import { useState, useEffect, useCallback, useRef } from 'react';
import { useExportManagement } from './useExportManagement';
import type { ExportHistoryItem } from 'types/report';

export interface ExportNotificationHookReturn {
  // Export data from useExportManagement
  exports: ExportHistoryItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  totalItems: number;
  error: string | null;

  // Notification state
  unreadCount: number;
  isProcessing: boolean;
  currentProgress: number | undefined;

  // Actions
  loadMoreExports: () => void;
  handleDownload: (item: ExportHistoryItem) => Promise<void>;
  handleDelete: (item: ExportHistoryItem) => Promise<void>;
  handleRetry: () => void;
  handleMarkAsSeen: () => void;
}

/**
 * Custom hook for managing export notifications with in-memory state
 * Simple approach: track last seen timestamp in memory, reset on panel open
 */
export const useExportNotifications = (): ExportNotificationHookReturn => {
  const exportManagement = useExportManagement();
  const { exports, handleDelete: originalHandleDelete } = exportManagement;

  // Track when user last opened the notification panel (in-memory only)
  const [lastSeenTimestamp, setLastSeenTimestamp] = useState<Date>(new Date());
  const prevExportsRef = useRef<ExportHistoryItem[]>([]);

  // Track processing exports and calculate average progress
  const processingExports = exports.filter(exp => exp.status === 'queued' || exp.status === 'processing');
  const isProcessing = processingExports.length > 0;

  const currentProgress = isProcessing
    ? processingExports.reduce((sum, exp) => sum + (exp.progress || 0), 0) / processingExports.length
    : undefined;

  // Calculate unread count: exports completed after last seen timestamp
  const unreadCount = exports.filter(exp => {
    if (exp.status !== 'completed') return false;
    const createdAt = new Date(exp.createdAt);
    return createdAt > lastSeenTimestamp;
  }).length;

  // Monitor for newly completed exports and update seen timestamp automatically
  // This ensures old completed exports don't show as unread on page refresh
  useEffect(() => {
    if (prevExportsRef.current.length === 0 && exports.length > 0) {
      // On initial load, mark current time as seen (don't show old exports as new)
      const hasAnyCompleted = exports.some(exp => exp.status === 'completed');
      if (hasAnyCompleted) {
        // Find the most recent completed export
        const mostRecentCompleted = exports
          .filter(exp => exp.status === 'completed')
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

        if (mostRecentCompleted) {
          // Set last seen to the most recent completed export time
          // This way only NEW completions after page load show badge
          setLastSeenTimestamp(new Date(mostRecentCompleted.createdAt));
        }
      }
      prevExportsRef.current = exports;
      return;
    }

    prevExportsRef.current = exports;
  }, [exports]);

  // Handle marking exports as seen (when user opens notification panel)
  const handleMarkAsSeen = useCallback(() => {
    setLastSeenTimestamp(new Date());
  }, []);

  // Wrap delete handler
  const handleDelete = useCallback(
    async (item: ExportHistoryItem) => {
      await originalHandleDelete(item);
    },
    [originalHandleDelete]
  );

  return {
    ...exportManagement,
    handleDelete,
    unreadCount,
    isProcessing,
    currentProgress,
    handleMarkAsSeen,
  };
};

export type UseExportNotificationsReturn = ReturnType<typeof useExportNotifications>;
